<template>
    <div class="item"
         :class="{completed : isFilled}"
         @click="activateInput"
         ref="paramContainerRef"
    >
        <div class="itemContent">
            <div>
                <label :for=label>{{ labelFormatted }}</label>
                <button v-if="tooltip"
                        class="tooltip-trigger"
                        @click.stop.prevent="showTooltip = !showTooltip"
                >
                    <svg width="15px"
                         height="15px"
                         viewBox="0 0 22 22"
                         xmlns="http://www.w3.org/2000/svg"
                         xml:space="preserve"
                         style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
                        <g transform="matrix(1,0,0,1,-25616.2,-8406.49)">
                            <g id="Miscellaneous" transform="matrix(4.0118,0,0,3.78149,19548.7,0)">
                                <rect x="1250" y="0" width="1200" height="3000" style="fill:none;"/>
                                <g transform="matrix(0.249264,0,0,0.264446,1512.08,2222.73)">
                                    <path
                                      d="M12,22.75C6.07,22.75 1.25,17.93 1.25,12C1.25,6.07 6.07,1.25 12,1.25C17.93,1.25 22.75,6.07 22.75,12C22.75,17.93 17.93,22.75 12,22.75ZM12,2.75C6.9,2.75 2.75,6.9 2.75,12C2.75,17.1 6.9,21.25 12,21.25C17.1,21.25 21.25,17.1 21.25,12C21.25,6.9 17.1,2.75 12,2.75Z"
                                      style="fill:rgb(41,45,50);fill-rule:nonzero;"/>
                                </g>
                                <g transform="matrix(0.249264,0,0,0.264446,-4872.81,0)">
                                    <path
                                      d="M25626.9,8406.49C25632.9,8406.49 25637.7,8411.32 25637.7,8417.26C25637.7,8423.21 25632.9,8428.04 25626.9,8428.04C25621,8428.04 25616.2,8423.21 25616.2,8417.26C25616.2,8411.32 25621,8406.49 25626.9,8406.49ZM25626.9,8423.49C25626.4,8423.49 25625.9,8423.04 25625.9,8422.51C25625.9,8421.97 25626.4,8421.53 25626.9,8421.53C25627.4,8421.53 25627.9,8421.97 25627.9,8422.51C25627.9,8423.04 25627.5,8423.49 25626.9,8423.49ZM25626.9,8420.23C25626.4,8420.23 25625.9,8419.78 25625.9,8419.25L25625.9,8418.97C25625.9,8417.45 25627,8416.71 25627.5,8416.42C25627.9,8416.09 25628.1,8415.87 25628.1,8415.53C25628.1,8414.88 25627.6,8414.34 25626.9,8414.34C25626.3,8414.34 25625.7,8414.88 25625.7,8415.53C25625.7,8416.07 25625.3,8416.51 25624.7,8416.51C25624.2,8416.51 25623.8,8416.07 25623.8,8415.53C25623.8,8413.79 25625.2,8412.38 25626.9,8412.38C25628.6,8412.38 25630.1,8413.79 25630.1,8415.53C25630.1,8417.02 25629,8417.77 25628.6,8418.04C25628,8418.38 25627.9,8418.6 25627.9,8418.97L25627.9,8419.25C25627.9,8419.8 25627.4,8420.23 25626.9,8420.23Z"
                                      style="fill:rgb(0,3,51);"/>
                                </g>
                            </g>
                        </g>
                    </svg>
                </button>
            </div>
            <div class="paramInput">
                <input type="text"
                       inputmode="numeric"
                       :id="id"
                       :value="modelValue"
                       :min="min"
                       :max="max"
                       @input="handleInput"
                       :required="required"
                       :placeholder="placeholder"
                />
                <span class="itemUnit" v-if="unit">{{ unit }}</span>
            </div>
        </div>
        <div v-if="showTooltip" class="tooltip-content">
            <div v-html="tooltip"></div>
        </div>
        <div class="errorContainer" v-if="errorMsg || localError">
            <span v-if="errorMsg">{{ errorMsg }}</span>
            <span v-if="formattedError">{{ formattedError }}</span>
        </div>
    </div>
</template>

<script>
import {computed, defineComponent, ref, onMounted, onUnmounted, watch} from "vue";
import {useGettext} from "vue3-gettext";

export default defineComponent({
    name: 'textInputField',
    emits: ['update:modelValue', 'initiate-recalculation'],
    props: {
        label: {
            type: String,
            required: false,
            default: ""
        },
        id: {
            type: String,
            required: true,
            default: ""
        },
        modelValue: {
            type: String,
            required: false,
            default: "",
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        min: {
            type: Number,
            required: false,
            default: null
        },
        max: {
            type: Number,
            required: false,
            default: null
        },
        unit: {
            type: String,
            required: false,
            default: null
        },
        tooltip: {
            type: String,
            required: false,
        },
        error: {
            type: Object,
            required: false
        }
    },
    setup(props, {emit}) {
        // translations
        const {$gettext} = useGettext();
        const gettext = $gettext;

        const isFilled = computed(() => {
            return !!inputValue.value
        })

        const labelFormatted = computed(() => {
            if (props.required) {
                return props.label + ' *'
            } else {
                return props.label
            }
        })

        const errorMsg = ref('')
        const localError = ref(null);
        // Watch for changes in the "error" prop from the parent component
        watch(() => props.error, (newError) => {
          localError.value = newError;
        });

        const activateInput = () => {
            // Focus the input field when the div.item is clicked
            const inputElement = document.getElementById(props.label);
            if (inputElement) {
                inputElement.focus();
                inputElement.select();
            }
        };

        const inputValue = ref(props.modelValue);

        // timer
        const delay = 2000; // 2 seconds
        let timerId;
        const counter = ref(delay);

        const validateAndProcessValue = (newValue) => {
            // Reset the timer when handleInput is called again within the delay
            clearTimeout(timerId);
            counter.value = delay;

            const isNumeric = /^-?[0-9]+$/.test(newValue);
            if (newValue && !isNumeric) {
                errorMsg.value = gettext('Hier kan u enkel een getal ingeven.');
            } else if (newValue && props.max !== null && props.min !== null && (newValue > props.max || newValue < props.min)) {
                errorMsg.value = gettext('De door u opgegeven waarde lijkt niet juist te zijn volgens onze gegevens.');
            } else {
                errorMsg.value = '';
            }

            // Process the newValue as desired
            inputValue.value = newValue;
            emit("update:modelValue", newValue);

            // Start the timer only if input value is numeric
            if (isNumeric) {
                timerId = setTimeout(() => {
                    emit('initiate-recalculation');
                }, delay);
            }
        };

        const handleInput = (event) => {
            localError.value = null;
            const newValue = event.target.value;
            validateAndProcessValue(newValue);
        };

        const handleClick = (value) => {
            validateAndProcessValue(value);
        };

        const placeholder = computed(() => {
            if (!inputValue.value) {
                return '0'
            } else {
                return ''
            }
        })

        const formattedError = computed(() => {
            if (props.error) {
                return props.error.translated_msg.replace('%s', props.error.translated_param);
            } else {
                return null
            }
        })

        const showTooltip = ref(false)
        const paramContainerRef = ref(null)
        const handleClickOutside = (event) => {
          const isClickedOutside = paramContainerRef.value && !paramContainerRef.value.contains(event.target);

          if (isClickedOutside) {
            showTooltip.value = false;
          }
        };

        onMounted(() => {
          document.addEventListener('click', handleClickOutside);
        });

        onUnmounted(() => {
          document.removeEventListener('click', handleClickOutside);
        });

        return {
            gettext,
            labelFormatted,
            isFilled,
            placeholder,
            activateInput,
            errorMsg,
            handleInput,
            formattedError,
            showTooltip,
            handleClick,
            paramContainerRef,
            localError
        }
    }
})
</script>

<style scoped lang="scss">
@import '@/styles/defaults.scss';

div.item {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px 15px;
    border: solid 1px #e0e0e0;
    border-radius: $border-radius;
    cursor: pointer;
}

div.itemContent {
    display: flex;
    justify-content: space-between;
}

div.itemContent > *:first-child {
    display: flex;
    align-items: center;
}

div.itemContent > div:last-of-type {
    display: inline-flex;
    flex: 1;
    justify-content: flex-end;
}

div.itemContent > div > label {
    cursor: pointer;
    margin-bottom: 0;
}

div.paramInput {
    display: flex;
    gap: 3px;
}

div.paramInput > input[type="text"] {
    all: unset;
    text-decoration: underline dotted 1px !important;
    text-align: right !important;
    text-underline-offset: 2px !important;
}

div.paramInput > input[type="text"] {
    max-width: 50px;
}

div.paramInput > input[type="text"]::placeholder {
    color: black;
    opacity: 1;
}

div.completed {
    background-color: #f7f7f7;
}

div.completed > div.itemContent > div.paramInput > input[type="text"] {
    border-bottom: none;
}

span.itemUnit {
    cursor: default;
}

/*1 Hides the arrows for input[type=number] */
/* Works for Chrome, Safari, Edge, Opera */
div.paramInput > input::-webkit-outer-spin-button,
div.paramInput > input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Works for Firefox */
div.paramInput > input[type="number"] {
    -moz-appearance: textfield;
    width: 100%;
}

/*1 end of reset */

div.errorContainer {
    font-size: 12px;
    margin: auto;
}

button.tooltip-trigger {
    /* remove default stying */
    background: none;
    border: none;
    padding: 0;
    /* end remove default stying */

    display: inline-block;
    cursor: pointer;
    margin-left: 5px;
    box-shadow: none;
}

button.tooltip-trigger > svg {
    vertical-align: middle;
}

div.tooltip-content {
    margin-top: 5px;
    color: $basic-text-color;
    font-size: 12px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}
</style>